import * as Sentry from "@sentry/react";
import React from "react";
import { privatePageSentryOptions, sanitizePrivateEvent } from "./sentry-private-page";
import { consoleEnvelopeTransport } from "./sentry-console-transport";
import { getPrivateTelemetry } from "./private-pages";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";

export const ENABLED_SENTRY_ENVIRONMENTS = ["prod", "qa"];
const currentMode = import.meta.env.MODE;
const logEnvelopes = import.meta.env.DEV &&
  !ENABLED_SENTRY_ENVIRONMENTS.includes(currentMode) &&
  import.meta.env.VITE_SENTRY_LOG_ENVELOPES === "true";
const sentryEnabled = ENABLED_SENTRY_ENVIRONMENTS.includes(currentMode) || logEnvelopes;
const transportOptions = logEnvelopes
  ? { dsn: "https://public@sentry.invalid/1", transport: consoleEnvelopeTransport }
  : { dsn: import.meta.env.VITE_SENTRY_DSN };

const initialPrivatePage = getPrivateTelemetry(window.location);

if (sentryEnabled && initialPrivatePage) {
  // Bearer-token pages use explicit timings and usage events, not automatic
  // instrumentation that can capture URLs, request bodies, or form contents.
  Sentry.init({
    ...transportOptions,
    environment: currentMode,
    release: `${__VERSION__}_${__REVISION__}`,
    ...privatePageSentryOptions(initialPrivatePage),
    tracesSampleRate: logEnvelopes ? 1 : 0.1,
  });
} else if (sentryEnabled) {
  Sentry.init({
    ...transportOptions,
    environment: currentMode,
    release: `${__VERSION__}_${__REVISION__}`,
    sendDefaultPii: true,
    // Client-side navigation must not send private-page data through the regular
    // telemetry client. Direct visits use the isolated client above.
    beforeSend: (event) => {
      const page = getPrivateTelemetry(window.location);
      return page ? sanitizePrivateEvent(event, page) : event;
    },
    beforeSendTransaction: (event) => getPrivateTelemetry(window.location) ? null : event,
    beforeSendLog: (log) => getPrivateTelemetry(window.location) ? null : log,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    enableLogs: true,
    tracesSampleRate:
      logEnvelopes ? 1 : currentMode === "qa" ? 0.1 : currentMode === "prod" ? 0.1 : 0.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Replay only buffers for errors, so it loads after the page load event instead of
// weighing down the entry chunk; errors before then get no replay.
export async function addReplayIntegration() {
  const { replayIntegration } = await import("@sentry-internal/replay");
  Sentry.addIntegration(replayIntegration({
    ...(logEnvelopes ? { useCompression: false } : {}),
    beforeErrorSampling: () => !getPrivateTelemetry(window.location),
  }));
}

if (sentryEnabled && !initialPrivatePage) {
  window.addEventListener("load", () => void addReplayIntegration(), { once: true });
}
